import { Box } from '@chakra-ui/react';
import {
  getMatchingStringsIgnoreCase,
  QueryFilters,
} from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { AllProjectSampleTypesGroup } from '@resistapp/common/comparable-sample-groups';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { FullProject } from '@resistapp/common/types';
import { sortUniqSamplingPointsAndAddSampleStatus } from '@resistapp/common/utils';
import { InputActionMeta } from 'chakra-react-select';
import { useEffect, useMemo, useState } from 'react';
import { FilterBarResistomapSelect as ResistomapSelect } from '../../forms/filter-bar-resistomap-select';
import { resistomapSelectClassNamePrefix } from '../../forms/resistomap-select';
import {
  buildChipSearchOptions,
  buildSampleTypeScopeOptions,
  getSelectedSampleSearchOption,
  SampleSearchOption,
  shouldShowSampleTypeScopeOptions,
} from './sample-search-utils';

interface Props {
  queryFilters: QueryFilters;
  project: FullProject;
}

export function SampleAndTypeSearch(props: Props) {
  const [inputValue, setInputValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectionLabel, setSelectionLabel] = useState('');
  const { samplesByUID, id: projectId } = props.project;
  const samplingPoints = useMemo(
    () => sortUniqSamplingPointsAndAddSampleStatus(samplesByUID, projectId),
    [samplesByUID, projectId],
  );
  const noSamplesWithPhrase = 'No samples with phrase';

  // Note hacky extension of Select to a simple search field
  // - the label has to match the input to be shown
  useEffect(() => {
    if (inputValue) {
      const envNames = samplingPoints.map(smplPnt => smplPnt.name);
      const matchingSmplPntNames = getMatchingStringsIgnoreCase(inputValue, envNames);
      const cnt = matchingSmplPntNames.length;

      const label =
        cnt > 1
          ? `Select ${cnt} samples with phrase '${inputValue.toLowerCase()}'`
          : cnt === 1
            ? `Select '${matchingSmplPntNames[0]}'`
            : `${noSamplesWithPhrase} '${inputValue.toLowerCase()}'`;

      setSelectionLabel(label);
    } else {
      setSelectionLabel(menuOpen ? 'Select all (or type to seach)' : 'All samples');
    }
  }, [inputValue, samplingPoints, menuOpen]);

  const allOrSelectedOption: SampleSearchOption = {
    label: selectionLabel,
    value: AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS,
    kind: 'all-samples',
  };
  const sampleTypeScopeOptions = useMemo(() => buildSampleTypeScopeOptions(samplingPoints), [samplingPoints]);
  const showSampleTypeOptions = shouldShowSampleTypeScopeOptions(sampleTypeScopeOptions);
  const chipOptions = useMemo(() => buildChipSearchOptions(props.project.chips), [props.project.chips]);

  const separator: SampleSearchOption = {
    label: '──────────',
    value: 'chip-separator',
    kind: 'separator',
    isDisabled: true,
  };

  const envSeparator: SampleSearchOption = {
    label: '──────────',
    value: 'type-separator',
    kind: 'separator',
    isDisabled: true,
  };

  const baseOptions: SampleSearchOption[] = !showSampleTypeOptions
    ? [allOrSelectedOption]
    : selectionLabel.startsWith(noSamplesWithPhrase)
      ? [...sampleTypeScopeOptions, envSeparator, allOrSelectedOption]
      : [allOrSelectedOption, envSeparator, ...sampleTypeScopeOptions];

  const options = chipOptions.length > 1 ? [...baseOptions, separator, ...chipOptions] : baseOptions;

  return (
    <Box
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
      className={TestSelectors.TEST_SAMPLE_SEARCH}
    >
      <ResistomapSelect<SampleSearchOption, false>
        options={options}
        size={'md'}
        isClearable={false}
        isDisabled={false}
        value={getSelectedSampleSearchOption(options, props.queryFilters.filters, samplingPoints)}
        onChange={option => {
          if (!option) {
            return;
          }

          switch (option.kind) {
            case 'chip':
              props.queryFilters.setChipStable(option.value);
              break;
            case 'sample-type':
              props.queryFilters.setSamplingPointScopeStable(option.sampleTypeGroup);
              props.queryFilters.setChipStable(undefined);
              break;
            case 'sample-subtype':
              props.queryFilters.setSamplingPointScopeStable(option.sampleTypeGroup, option.samplingPointIds);
              props.queryFilters.setChipStable(undefined);
              break;
            case 'all-samples': {
              const envNames = samplingPoints.map(smplPnt => smplPnt.name);
              const matchingSmplPntNames = inputValue ? getMatchingStringsIgnoreCase(inputValue, envNames) : [];
              if (matchingSmplPntNames.length) {
                props.queryFilters.setMatchingSamplingPointsAcrossTypes(inputValue);
              } else {
                props.queryFilters.setSamplingPointScopeStable(AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS);
              }
              props.queryFilters.setChipStable(undefined);
              break;
            }
            case 'separator':
              return;
          }

          setInputValue('');
        }}
        classNamePrefix={resistomapSelectClassNamePrefix}
        formatOptionLabel={(option, { context }) =>
          context === 'menu' && option.kind === 'sample-subtype' ? <Box pl={6}>{option.label}</Box> : option.label
        }
        onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
          if (actionMeta.action === 'input-change') {
            setInputValue(newValue);
          }
        }}
        onMenuOpen={() => {
          setMenuOpen(true);
        }}
        onMenuClose={() => {
          setMenuOpen(false);
        }}
        isOptionDisabled={(option, _selectValue) =>
          option.kind === 'separator' || option.label.startsWith(noSamplesWithPhrase)
        }
      />
    </Box>
  );
}
